import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';

import {
  Event as TemplateEvent,
  FieldMapping,
  MatchCondition,
  PayloadSchemaField,
} from '../../../shared/models/event.model';
import { ToastService } from '../../../shared/components/toast/toast.service';

/**
 * SMS-013: Modal para criar/editar eventos de integração com:
 * - payload_example (JSON) -> auto-derivação de paths
 * - match_condition (campo = select com paths)
 * - mapping (lead column <- source path)
 *
 * Substitui o conceito antigo (predicate + default_lead_list_id + niches + is_fallback).
 */
@Component({
  selector: 'app-event-editor-modal',
  templateUrl: './event-editor-modal.component.html',
  styleUrls: ['./event-editor-modal.component.scss']
})
export class IntegrationTemplateEventEditorModalComponent implements OnInit, OnDestroy {

  @Input() event: TemplateEvent | null = null;

  @Output() save = new EventEmitter<TemplateEvent>();

  form: FormGroup;

  /** Paths achatados a partir do payload_example JSON, usados nos selects de condição e mapping. */
  availablePaths: string[] = [];

  /** Valor escalar encontrado em cada path do JSON — usado pra auto-preencher o valor da condição. */
  pathValues: Record<string, string> = {};

  /** Colunas canônicas do lead disponíveis pra mapeamento (espelham o model Lead). */
  readonly leadColumns = [
    { value: 'name', label: 'Nome' },
    { value: 'phone', label: 'Telefone' },
    { value: 'email', label: 'Email' },
    { value: 'product_name', label: 'Nome do Produto' },
    { value: 'product_price', label: 'Preço do Produto' },
    { value: 'product_url', label: 'URL do Produto' },
    { value: 'checkout_url', label: 'URL de Checkout' },
    { value: 'ticket_url', label: 'URL do Boleto' },
    { value: 'ticket_number', label: 'Número do Boleto' },
    { value: 'ticket_date', label: 'Vencimento do Boleto' },
    { value: 'pix_url', label: 'URL do PIX' },
    { value: 'pix_code', label: 'Código do PIX' },
    { value: 'members_url', label: 'URL de Área de Membros' },
    { value: 'customized_url', label: 'URL Personalizada' },
    { value: 'tracking_url', label: 'URL de Rastreio' },
    { value: 'tracking_code', label: 'Código de Rastreio' },
    { value: 'var1', label: 'Variável 1' },
    { value: 'var2', label: 'Variável 2' },
    { value: 'var3', label: 'Variável 3' },
    { value: 'var4', label: 'Variável 4' },
    { value: 'var5', label: 'Variável 5' },
  ];

  /** Aliases de chave por coluna do lead — usados pra detectar o path no payload. */
  private readonly mappingAliases: Record<string, string[]> = {
    name: ['name', 'nome', 'fullname', 'full_name', 'customer_name', 'buyer_name', 'client_name', 'cliente', 'first_name'],
    phone: ['phone', 'telephone', 'telefone', 'tel', 'celular', 'cellphone', 'cell', 'mobile', 'whatsapp', 'msisdn', 'phone_number'],
    email: ['email', 'e_mail', 'mail', 'email_address'],
  };

  /** Qualificadores que indicam que o campo NÃO é do lead (produto, loja, vendedor...). */
  private readonly mappingPathBlocklist = [
    'product', 'produto', 'plan', 'plano', 'offer', 'oferta', 'item',
    'store', 'loja', 'seller', 'vendedor', 'affiliate', 'afiliado',
    'producer', 'produtor', 'support', 'suporte',
  ];

  /** Qualificadores que reforçam que o path é do lead/comprador. */
  private readonly mappingPathBoost = [
    'customer', 'buyer', 'client', 'cliente', 'comprador', 'lead', 'contact', 'contato',
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    public modal: NgbActiveModal,
    private toastService: ToastService
  ) {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(120)]],
      payload_example: ['', Validators.required],
      payload_schema: [[] as PayloadSchemaField[]],
      match_condition: this.fb.array([], Validators.minLength(1)),
      mapping: this.fb.array([]),
      sort_order: [0],
    });
  }

  ngOnInit(): void {
    if (this.event) {
      this.fillForm(this.event);
    } else {
      // Novo evento: adicionar 1 condição vazia obrigatória
      this.addCondition();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ----- JSON parser / paths -----

  onPayloadExampleBlur(): void {
    const jsonStr = this.form.get('payload_example')?.value;
    this.derivePathsFromJSON(jsonStr);
    this.autofillMapping();
  }

  /**
   * Parseia o JSON do payload_example e popula `availablePaths` + `payload_schema`.
   * Tolerante: JSON inválido apenas zera os paths e mostra toast.
   */
  derivePathsFromJSON(jsonStr: string): void {
    if (!jsonStr || !jsonStr.trim()) {
      this.availablePaths = [];
      this.pathValues = {};
      this.form.patchValue({ payload_schema: [] });
      return;
    }

    try {
      const obj = JSON.parse(jsonStr);
      this.availablePaths = this.flattenPaths(obj);
      this.pathValues = {};
      for (const p of this.availablePaths) {
        const v = this.getValueAtPath(obj, p);
        if (v !== null && v !== undefined && typeof v !== 'object') {
          this.pathValues[p] = String(v);
        }
      }
      const schema: PayloadSchemaField[] = this.availablePaths.map(p => ({
        field: p,
        label: p,
        type: 'string',
        required: false,
      }));
      this.form.patchValue({ payload_schema: schema });
    } catch (e) {
      this.availablePaths = [];
      this.pathValues = {};
      this.form.patchValue({ payload_schema: [] });
      this.toastService.info('JSON inválido — corrija o exemplo de payload');
    }
  }

  private flattenPaths(obj: any, prefix = ''): string[] {
    let paths: string[] = [];
    if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) {
      return paths;
    }
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const newKey = prefix ? `${prefix}.${key}` : key;
        paths.push(newKey);
        if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
          paths = paths.concat(this.flattenPaths(obj[key], newKey));
        }
      }
    }
    return paths;
  }

  private getValueAtPath(obj: any, path: string): any {
    return path.split('.').reduce(
      (acc, key) => (acc === null || acc === undefined ? undefined : acc[key]),
      obj,
    );
  }

  // ----- Match Condition FormArray -----

  get conditionsFormArray(): FormArray {
    return this.form.get('match_condition') as FormArray;
  }

  addCondition(): void {
    const control = this.fb.group({
      field: ['', Validators.required],
      operator: ['=', Validators.required],
      value: [''],
    });
    this.conditionsFormArray.push(control);
  }

  /**
   * Ao selecionar o campo de uma condição, auto-preenche o valor com o que está
   * naquele path do JSON de exemplo. Operador `exists` não usa valor.
   */
  onConditionFieldChange(index: number): void {
    const group = this.conditionsFormArray.at(index);
    const field = group.get('field')?.value;
    const operator = group.get('operator')?.value;
    if (!field || operator === 'exists') {
      return;
    }
    const value = this.pathValues[field];
    if (value !== undefined) {
      group.get('value')?.setValue(value);
    }
  }

  removeCondition(index: number): void {
    if (this.conditionsFormArray.length > 1) {
      this.conditionsFormArray.removeAt(index);
    } else {
      this.toastService.info('Pelo menos uma condição é obrigatória');
    }
  }

  get isConditionValid(): boolean {
    return this.conditionsFormArray.length >= 1 && this.conditionsFormArray.valid;
  }

  // ----- Mapping FormArray -----

  get mappingFormArray(): FormArray {
    return this.form.get('mapping') as FormArray;
  }

  addMapping(): void {
    this.mappingFormArray.push(this.fb.group({
      leadColumn: ['', Validators.required],
      sourceField: ['', Validators.required],
    }));
  }

  /**
   * Verifica se uma coluna do lead já foi escolhida em outra linha de mapeamento.
   * Usado pra desabilitar a opção no dropdown e evitar mapear dois paths pro mesmo campo.
   */
  isLeadColumnUsed(value: string, currentIndex: number): boolean {
    return this.mappingFormArray.controls.some(
      (ctrl, i) => i !== currentIndex && ctrl.get('leadColumn')?.value === value,
    );
  }

  removeMapping(index: number): void {
    this.mappingFormArray.removeAt(index);
  }

  /**
   * SMS-006: tenta preencher o mapeamento de nome, telefone e email a partir
   * dos paths do payload de exemplo. Combina o nome da chave (aliases) com o
   * formato do valor; só preenche colunas ainda não mapeadas.
   */
  private autofillMapping(): void {
    if (this.availablePaths.length === 0) {
      return;
    }
    let filled = 0;
    for (const column of ['name', 'phone', 'email']) {
      if (this.isLeadColumnMapped(column)) {
        continue;
      }
      const path = this.detectPathForColumn(column);
      if (path) {
        this.mappingFormArray.push(this.fb.group({
          leadColumn: [column, Validators.required],
          sourceField: [path, Validators.required],
        }));
        filled++;
      }
    }
    if (filled > 0) {
      this.toastService.info(
        `${filled} campo(s) de mapeamento preenchidos automaticamente — confira antes de salvar`,
      );
    }
  }

  private isLeadColumnMapped(column: string): boolean {
    return this.mappingFormArray.controls.some(
      (c) => c.get('leadColumn')?.value === column,
    );
  }

  /**
   * Escolhe o melhor path pra uma coluna do lead: precisa de alias no nome da
   * chave E formato de valor compatível. Path que cita produto/loja/vendedor é
   * descartado; path sob customer/buyer/cliente ganha bônus de score.
   */
  private detectPathForColumn(column: string): string | null {
    const aliases = this.mappingAliases[column] || [];
    let best: { path: string; score: number } | null = null;

    for (const path of this.availablePaths) {
      const value = this.pathValues[path];
      if (value === undefined) {
        continue; // só paths escalares
      }
      if (!this.valueMatchesColumn(column, value)) {
        continue;
      }
      const lowerPath = path.toLowerCase();
      if (this.mappingPathBlocklist.some((w) => lowerPath.includes(w))) {
        continue;
      }

      const segmentNorm = this.normalizeKey(path.split('.').pop() || '');
      const words = this.segmentWords(path);
      const exactHit = aliases.some((a) => this.normalizeKey(a) === segmentNorm);
      const wordHit = aliases.some((a) => words.includes(this.normalizeKey(a)));
      if (!exactHit && !wordHit) {
        continue;
      }

      let score = exactHit ? 3 : 1;
      if (this.mappingPathBoost.some((w) => lowerPath.includes(w))) {
        score += 2;
      }
      if (!best || score > best.score) {
        best = { path, score };
      }
    }
    return best ? best.path : null;
  }

  /** Validação leve do formato do valor pra confirmar o palpite do path. */
  private valueMatchesColumn(column: string, value: string): boolean {
    const v = (value ?? '').trim();
    if (!v) {
      return false;
    }
    switch (column) {
      case 'phone': {
        const digits = v.replace(/\D/g, '');
        return digits.length >= 8 && digits.length <= 15;
      }
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
      case 'name':
        return /[a-zA-ZÀ-ÿ]/.test(v) && !/^\d+$/.test(v);
      default:
        return true;
    }
  }

  private normalizeKey(s: string): string {
    return s.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  /** Quebra o último segmento do path em palavras (snake_case e camelCase). */
  private segmentWords(path: string): string[] {
    const seg = path.split('.').pop() || '';
    return seg
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean);
  }

  // ----- Save / Cancel -----

  onSave(): void {
    if (!this.form.valid || !this.isConditionValid) {
      this.form.markAllAsTouched();
      this.toastService.info('Preencha os campos obrigatórios');
      return;
    }

    const raw = this.form.getRawValue();
    const eventData: TemplateEvent = {
      id: this.event?.id,
      name: (raw.name ?? '').trim(),
      payload_example: raw.payload_example,
      payload_schema: raw.payload_schema || [],
      match_condition: (raw.match_condition || []) as MatchCondition[],
      mapping: (raw.mapping || []) as FieldMapping[],
      sort_order: raw.sort_order ?? 0,
    };

    this.save.emit(eventData);
    this.modal.close(eventData);
  }

  onCancel(): void {
    this.modal.dismiss();
  }

  get title(): string {
    return this.event ? 'Editar Evento' : 'Novo Evento';
  }

  /**
   * Restaura o form a partir de um event existente (edição).
   * Aceita campos novos (SMS-013) e legados (retrocompat com dados antigos do backend).
   */
  private fillForm(event: TemplateEvent): void {
    // Match condition: aceita `match_condition` ou legacy `predicate`.
    const rawConditions: any[] =
      (event.match_condition && event.match_condition.length > 0)
        ? event.match_condition
        : (event.predicate && event.predicate.length > 0 ? event.predicate : []);

    if (rawConditions.length > 0) {
      rawConditions.forEach((cond) => {
        this.conditionsFormArray.push(this.fb.group({
          field: [cond.field, Validators.required],
          operator: [this.normalizeOperator(cond.operator), Validators.required],
          value: [cond.value ?? ''],
        }));
      });
    } else {
      this.addCondition();
    }

    // Mapping (FormArray)
    if (event.mapping && event.mapping.length > 0) {
      event.mapping.forEach((map: any) => {
        this.mappingFormArray.push(this.fb.group({
          leadColumn: [map.leadColumn || map.lead_column || '', Validators.required],
          sourceField: [map.sourceField || map.source_field || '', Validators.required],
        }));
      });
    }

    // payload_example pode vir como string (textarea cru) ou como objeto/array (cast
    // 'array' do model). O textarea precisa de string — stringify quando for objeto.
    const rawExample: unknown = event.payload_example;
    const payloadExampleStr =
      typeof rawExample === 'string'
        ? rawExample
        : (rawExample ? JSON.stringify(rawExample, null, 2) : '');

    this.form.patchValue({
      name: event.name || '',
      payload_example: payloadExampleStr,
      payload_schema: event.payload_schema || [],
      sort_order: event.sort_order ?? 0,
    });

    // Se já houver payload_example, dispara a derivação de paths.
    if (payloadExampleStr) {
      this.derivePathsFromJSON(payloadExampleStr);
    }
  }

  /**
   * Operadores antigos em PT (`contém`, `existe`) viraram canônicos em EN (`contains`, `exists`).
   */
  private normalizeOperator(op: string): string {
    switch (op) {
      case 'contém': return 'contains';
      case 'existe': return 'exists';
      default: return op;
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
}
